import React, { useState } from 'react';
import {
  FiSave, FiRotateCcw, FiTrash2, FiEye, FiEyeOff, FiAlertCircle,
  FiEdit3, FiSliders, FiSettings, FiArrowLeft,
} from 'react-icons/fi';
import { Button, Badge, Toggle } from '../../../components/common';
import CmsField from './CmsField';
import SectionItemsManager from './SectionItemsManager';
import { SECTION_FIELDS, SECTION_TYPE_OPTIONS, getSectionType } from '../sectionTypes';
import './SectionEditor.css';

const TABS = [
  { key: 'content', label: 'Content', icon: FiEdit3 },
  { key: 'design', label: 'Design', icon: FiSliders },
  { key: 'advanced', label: 'Advanced', icon: FiSettings },
];

const isBlank = (value) => value === undefined || value === null || value === '';

/**
 * Right-hand pane of the page editor: edits one section, its `settings`
 * JSON and its repeatable items.
 */
const SectionEditor = ({
  section,
  draft,
  dirty,
  saving,
  onChange,
  onSave,
  onRevert,
  onDelete,
  onToggleStatus,
  onItemsChange,
  onBack,
}) => {
  const [tab, setTab] = useState('content');
  const [rawJson, setRawJson] = useState(false);
  const [jsonError, setJsonError] = useState('');

  const typeConfig = getSectionType(draft.type);
  const primaryFields = typeConfig.fields || [];
  const otherFields = Object.keys(SECTION_FIELDS).filter((field) => !primaryFields.includes(field));
  const usedOtherFields = otherFields.filter((field) => !isBlank(draft[field]));

  const setField = (field, value) => onChange({ [field]: value });

  const setSetting = (key, value) =>
    onChange({ settings: { ...(draft.settings || {}), [key]: value } });

  const handleRawJsonChange = (text) => {
    onChange({ settingsRaw: text });
    if (!text.trim()) {
      setJsonError('');
      onChange({ settings: {}, settingsRaw: text });
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setJsonError('');
        onChange({ settings: parsed, settingsRaw: text });
      } else {
        setJsonError('Settings must be a JSON object, e.g. { "align": "center" }');
      }
    } catch {
      setJsonError('Invalid JSON — the section will not save until this is fixed.');
    }
  };

  const renderField = (field) => {
    const config = SECTION_FIELDS[field];
    if (!config) return null;
    return (
      <CmsField
        key={field}
        type={config.type}
        label={config.label}
        placeholder={config.placeholder}
        value={draft[field]}
        onChange={(value) => setField(field, value)}
      />
    );
  };

  return (
    <div className="section-editor">
      <header className="se-header">
        <button type="button" className="se-back" onClick={onBack} aria-label="Back to sections">
          <FiArrowLeft />
        </button>

        <div className="se-heading">
          <div className="se-title-row">
            <h3 className="se-title">{draft.title || typeConfig.label}</h3>
            <Badge variant="primary" size="small">{typeConfig.label}</Badge>
          </div>
          <div className="se-meta">
            <code>{draft.sectionKey}</code>
            {typeConfig.hint && <span className="se-hint">{typeConfig.hint}</span>}
          </div>
        </div>

        <div className="se-header-actions">
          <button
            type="button"
            className={`se-status ${draft.isActive ? 'on' : ''}`}
            onClick={onToggleStatus}
            title={draft.isActive ? 'Published on the site — click to hide' : 'Hidden from the site — click to publish'}
          >
            {draft.isActive ? <FiEye /> : <FiEyeOff />}
            <span>{draft.isActive ? 'Published' : 'Hidden'}</span>
          </button>
          <button type="button" className="se-icon-btn danger" title="Delete section" onClick={onDelete}>
            <FiTrash2 />
          </button>
        </div>
      </header>

      <nav className="se-tabs" role="tablist">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={tab === item.key}
            className={`se-tab ${tab === item.key ? 'active' : ''}`}
            onClick={() => setTab(item.key)}
          >
            <item.icon />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      <div className="se-body">
        {tab === 'content' && (
          <div className="se-panel">
            <div className="se-fields">{primaryFields.map(renderField)}</div>

            {usedOtherFields.length > 0 && (
              <details className="se-details">
                <summary>Other filled fields ({usedOtherFields.length})</summary>
                <div className="se-fields">{usedOtherFields.map(renderField)}</div>
              </details>
            )}

            {typeConfig.items?.enabled && section.id && (
              <div className="se-items">
                <SectionItemsManager section={{ ...section, type: draft.type }} onItemsChange={onItemsChange} />
              </div>
            )}
          </div>
        )}

        {tab === 'design' && (
          <div className="se-panel">
            <div className="se-panel-head">
              <div>
                <h4>Display settings</h4>
                <p>Stored in the section's <code>settings</code> JSON and read by the website.</p>
              </div>
              <Toggle
                name="raw-json"
                size="small"
                checked={rawJson}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setRawJson(enabled);
                  if (enabled) onChange({ settingsRaw: JSON.stringify(draft.settings || {}, null, 2) });
                  else setJsonError('');
                }}
                label="Edit as JSON"
              />
            </div>

            {rawJson ? (
              <CmsField
                type="json"
                label="Settings JSON"
                rows={10}
                value={draft.settingsRaw ?? JSON.stringify(draft.settings || {}, null, 2)}
                onChange={handleRawJsonChange}
                error={jsonError}
                hint={jsonError ? undefined : 'Keys are free-form — the website reads what it needs.'}
              />
            ) : typeConfig.settings?.length ? (
              <div className="se-fields se-fields--grid">
                {typeConfig.settings.map((setting) => (
                  <CmsField
                    key={setting.key}
                    type={setting.type}
                    label={setting.label}
                    options={setting.options}
                    value={draft.settings?.[setting.key] ?? setting.default ?? ''}
                    onChange={(value) => setSetting(setting.key, value)}
                    placeholder={setting.type === 'select' ? undefined : setting.placeholder}
                  />
                ))}
              </div>
            ) : (
              <p className="se-note">
                <FiAlertCircle /> This section type has no display settings. Switch on “Edit as JSON” to add custom keys.
              </p>
            )}
          </div>
        )}

        {tab === 'advanced' && (
          <div className="se-panel">
            <div className="se-fields se-fields--grid">
              <CmsField
                type="text"
                label="Section Key"
                value={draft.sectionKey}
                onChange={(value) => setField('sectionKey', value.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase())}
                hint="Machine name the website uses to find this block. Unique per page."
                required
              />
              <CmsField
                type="select"
                label="Section Type"
                value={draft.type}
                options={SECTION_TYPE_OPTIONS}
                onChange={(value) => setField('type', value)}
                hint="Changing the type changes which fields the website renders."
              />
              <CmsField
                type="number"
                label="Sort Order"
                value={draft.sortOrder}
                onChange={(value) => setField('sortOrder', value)}
                hint="Set automatically when you drag sections in the list."
              />
            </div>

            {otherFields.length > 0 && (
              <details className="se-details" open>
                <summary>All content fields</summary>
                <div className="se-fields">{otherFields.map(renderField)}</div>
              </details>
            )}
          </div>
        )}
      </div>

      <footer className={`se-footer ${dirty ? 'dirty' : ''}`}>
        <span className="se-dirty-note">
          {dirty ? 'You have unsaved changes' : 'All changes saved'}
        </span>
        <div className="se-footer-actions">
          <Button variant="ghost" icon={<FiRotateCcw />} onClick={onRevert} disabled={!dirty || saving}>
            Revert
          </Button>
          <Button icon={<FiSave />} onClick={onSave} loading={saving} disabled={!dirty || Boolean(jsonError)}>
            Save section
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default SectionEditor;
